import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { api } from './api/client';
import type { UserSummary } from './api/types';

/**
 * Session state.
 *
 * The JWT is an HttpOnly cookie the browser never exposes to JavaScript, so
 * "am I signed in?" is a question only the server can answer: `GET /auth/me`
 * either succeeds or 401s. We model that as a normal query so the router can
 * await it in `beforeLoad` and every component reads the same cached identity.
 *
 * Anything gated here is gated for *ergonomics only* — the API re-enforces
 * every capability. Never treat a hidden button as a security boundary.
 */

export type Role = 'cashier' | 'supervisor' | 'manager' | 'admin';

/** Mirrors ROLE_LEVEL in server/api/src/lib/auth.ts. Unknown roles fail closed. */
const ROLE_LEVEL: Record<string, number> = {
  cashier: 1,
  supervisor: 2,
  manager: 3,
  admin: 4,
};

export function roleLevel(role: string | null | undefined): number {
  return (role && ROLE_LEVEL[role]) || 0;
}

export interface SessionUser {
  id: number;
  username: string;
  name: string;
  role: Role | string;
}

/** Keep the cache to the session fields even though `/auth/me` now returns a safe profile DTO. */
function narrow(row: UserSummary): SessionUser {
  const first = row.firstName;
  const last = row.lastName;
  return {
    id: row.id,
    username: row.username,
    name: `${first} ${last}`.trim() || row.username,
    role: row.role,
  };
}

export const sessionQuery = {
  queryKey: ['session'] as const,
  queryFn: async () => narrow(await api.get<UserSummary>('/auth/me')),
  staleTime: 5 * 60_000,
  retry: false,
};

/** Used by the router's `beforeLoad`; returns null instead of throwing. */
export async function loadSession(qc: QueryClient): Promise<SessionUser | null> {
  try {
    return await qc.ensureQueryData(sessionQuery);
  } catch {
    return null;
  }
}

export function useSession(): SessionUser | null {
  const { data } = useQuery({ ...sessionQuery, enabled: true });
  return data ?? null;
}

export function useAuth() {
  const qc = useQueryClient();
  const user = useSession();

  const signIn = useMutation({
    mutationFn: async (creds: { username: string; password: string }) => {
      const result = await api.post<{
        user: { id: number; username: string; role: string; name: string };
      }>('/auth/login', { ...creds, responseMode: 'cookie' }, { noRefresh: true });
      return {
        id: result.user.id,
        username: result.user.username,
        name: result.user.name,
        role: result.user.role,
      } satisfies SessionUser;
    },
    onSuccess: (next) => {
      qc.setQueryData(sessionQuery.queryKey, next);
    },
  });

  const signOut = useMutation({
    mutationFn: async () => {
      // The cookie is HttpOnly — only the server can clear it.
      await api.post('/auth/logout', undefined, { noRefresh: true }).catch(() => {
        // Even a failed call still ends the local session: the operator asked to
        // leave, and the next guarded request will bounce them to /login.
      });
    },
    onSettled: () => {
      qc.clear();
      window.location.href = '/login';
    },
  });

  return {
    user,
    signIn,
    signOut,
    /** UI gating only. The server is the authority. */
    atLeast: (role: Role) => roleLevel(user?.role) >= roleLevel(role),
  };
}

/** Convenience for conditionally rendering manager/admin-only affordances. */
export function useAtLeast(role: Role): boolean {
  return useAuth().atLeast(role);
}
